import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const PANEL_IMG =
  "https://images.unsplash.com/photo-1474674556023-efef886fa147?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxtZXRhbCUyMGluZ290cyUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzg0OTczMjY3fDA&ixlib=rb-4.1.0&q=85";

const lineEase = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: lineEase }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pb-16 pt-32 md:pt-40"
    >
      {/* soft vibrant fluid blobs */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden>
        <div
          className="fluid-blob animate-blob left-[-6%] top-[6%] h-[42vw] w-[42vw] opacity-50"
          style={{
            background:
              "radial-gradient(circle at 40% 40%, #f6c9ac, #e0724c 55%, transparent 72%)",
          }}
        />
        <div
          className="fluid-blob animate-blob right-[-4%] top-[30%] h-[34vw] w-[34vw] opacity-35"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, #9fe6dc, #0e8f80 55%, transparent 74%)",
            animationDelay: "-7s",
          }}
        />
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="mb-8 flex items-center gap-4 font-mono-tech text-xs uppercase tracking-[0.25em] text-[var(--mx-ink-2)]"
        >
          <span className="h-px w-10 bg-[var(--mx-copper)]" />
          Non-Ferrous Recycling · Circular Metals
        </motion.div>

        <h1 className="font-display text-[13vw] font-black uppercase leading-[0.86] tracking-tighter text-[var(--mx-ink)] md:text-[8.5vw]">
          <Line delay={0.35}>
            Non-ferrous <span className="text-metal">scrap,</span>
          </Line>
          <Line delay={0.5}>responsibly</Line>
          <Line delay={0.65}>recycled.</Line>
        </h1>

        <div className="mt-12 grid items-end gap-10 md:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9 }}
            className="md:col-span-6"
          >
            <p className="max-w-md text-base leading-relaxed text-[var(--mx-ink-2)] md:text-lg">
              Metalix XT manufactures high-purity refined lead and custom lead
              alloys from recycled batteries and scrap — at our 11-acre,
              36,000 MT/year facility in Bharuch, Gujarat.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#quote"
                data-testid="hero-quote-cta"
                data-cursor="view"
                className="group relative overflow-hidden rounded-full bg-[var(--mx-copper)] px-7 py-3.5 font-mono-tech text-xs uppercase tracking-widest text-white"
              >
                <span className="relative z-10">Request a quote →</span>
                <span className="absolute inset-0 -translate-x-full bg-[var(--mx-charcoal)] transition-transform duration-300 group-hover:translate-x-0" />
              </a>
              <a
                href="#products"
                data-testid="hero-scroll-cta"
                className="font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)] underline-offset-4 transition-colors hover:text-[var(--mx-copper)] hover:underline"
              >
                See our products
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 1 }}
            className="md:col-span-6"
          >
            <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-[var(--mx-charcoal)] ring-1 ring-[var(--mx-line)] shadow-[0_30px_80px_-40px_rgba(27,23,20,0.5)]">
              <motion.img
                src={PANEL_IMG}
                alt="Molten metal in an industrial foundry"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#17130f]/60 via-transparent to-[#e0724c]/20" />
              <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-full bg-[var(--mx-bg)]/85 px-4 py-2 backdrop-blur-md">
                <span className="h-2 w-2 rounded-full bg-[var(--mx-teal)]" />
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--mx-ink)]">
                  GPCB &amp; MoEF compliant
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
